/**
 * 路由工具函数
 *
 * 提供路由相关的辅助功能
 */

import { RouteLocationNormalized, Router } from "vue-router";

/**
 * 路由历史记录管理
 */
class RouteHistory {
  private history: string[] = [];
  private maxSize = 10;

  /**
   * 添加路由记录
   */
  push(path: string): void {
    // 避免重复记录相同路径
    if (this.history[this.history.length - 1] !== path) {
      this.history.push(path);

      // 限制历史记录大小
      if (this.history.length > this.maxSize) {
        this.history.shift();
      }
    }
  }

  /**
   * 获取上一个路由
   */
  getPrevious(): string | undefined {
    if (this.history.length >= 2) {
      return this.history[this.history.length - 2];
    }
    return undefined;
  }

  /**
   * 获取所有历史记录
   */
  getAll(): string[] {
    return [...this.history];
  }

  /**
   * 清空历史记录
   */
  clear(): void {
    this.history = [];
  }
}

export const routeHistory = new RouteHistory();

/**
 * 判断是否是外部链接
 */
export function isExternal(path: string): boolean {
  return /^(https?:|mailto:|tel:)/.test(path);
}

/**
 * 判断路由是否需要认证
 */
export function requiresAuth(route: RouteLocationNormalized): boolean {
  return route.meta.requiresAuth === true;
}

/**
 * 判断路由是否在白名单中
 */
export function isInWhiteList(path: string, whiteList: string[]): boolean {
  return whiteList.includes(path);
}

/**
 * 获取重定向路径
 */
export function getRedirectPath(
  route: RouteLocationNormalized,
): string | undefined {
  const redirect = route.query.redirect as string;
  return redirect ? decodeURIComponent(redirect) : undefined;
}

/**
 * 构建带重定向的登录路径
 */
export function buildLoginPath(currentPath: string): string {
  const redirect = encodeURIComponent(currentPath);
  return `/login?redirect=${redirect}`;
}

/**
 * 获取路由标题
 */
export function getRouteTitle(
  route: RouteLocationNormalized,
  defaultTitle = "未命名页面",
): string {
  return (route.meta.title as string) || (route.name as string) || defaultTitle;
}

/**
 * 生成面包屑数据
 */
export interface BreadcrumbItem {
  title: string;
  path?: string;
  icon?: string;
}

export function generateBreadcrumb(
  route: RouteLocationNormalized,
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [];

  // 添加首页
  breadcrumbs.push({
    title: "首页",
    path: "/",
    icon: "el-icon-house",
  });

  // 从路由meta中获取面包屑
  if (route.meta.breadcrumb && Array.isArray(route.meta.breadcrumb)) {
    const metaBreadcrumb = route.meta.breadcrumb as string[];
    metaBreadcrumb.forEach((title, index) => {
      if (index === metaBreadcrumb.length - 1) {
        // 最后一项不可点击
        breadcrumbs.push({ title });
      } else {
        breadcrumbs.push({ title });
      }
    });
  } else {
    // 使用路由标题
    const title = getRouteTitle(route);
    if (title !== "首页") {
      breadcrumbs.push({ title });
    }
  }

  return breadcrumbs;
}

/**
 * 路由跳转封装（支持历史记录）
 */
export async function navigateTo(
  router: Router,
  path: string | { path: string; query?: Record<string, any> },
): Promise<void> {
  try {
    if (typeof path === "string") {
      await router.push(path);
      routeHistory.push(path);
    } else {
      await router.push(path);
      routeHistory.push(path.path);
    }
  } catch (error) {
    console.error("路由跳转失败:", error);
    throw error;
  }
}

/**
 * 路由替换封装
 */
export async function replaceTo(
  router: Router,
  path: string | { path: string; query?: Record<string, any> },
): Promise<void> {
  try {
    if (typeof path === "string") {
      await router.replace(path);
    } else {
      await router.replace(path);
    }
  } catch (error) {
    console.error("路由替换失败:", error);
    throw error;
  }
}

/**
 * 返回上一页
 */
export function goBack(router: Router): void {
  const prevPath = routeHistory.getPrevious();
  if (prevPath) {
    router.back();
  } else {
    // 没有历史记录，跳转到首页
    router.push("/");
  }
}

/**
 * 刷新当前页面
 */
export async function refreshPage(router: Router): Promise<void> {
  const currentPath = router.currentRoute.value.fullPath;

  // 跳转到一个空路由再返回，实现页面刷新
  await router.replace("/refresh");
  await router.replace(currentPath);
}

/**
 * 判断是否有返回路径
 */
export function hasBackRoute(): boolean {
  return routeHistory.getAll().length > 1;
}

/**
 * 路由缓存键生成器
 */
export function getRouteCacheKey(route: RouteLocationNormalized): string {
  return route.meta.keepAlive
    ? `${route.name as string}_${JSON.stringify(route.params)}`
    : "";
}

/**
 * 权限检查
 */
export function checkRoutePermission(
  route: RouteLocationNormalized,
  userRoles: string[],
): boolean {
  const requiredRoles = route.meta.roles as string[] | undefined;

  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }

  return requiredRoles.some((role) => userRoles.includes(role));
}

/**
 * 获取所有可访问的路由（根据权限过滤）
 */
export function getAccessibleRoutes(
  routes: unknown[],
  userRoles: string[],
): unknown[] {
  return routes.filter((route) => {
    const requiredRoles = (route as { meta?: { roles?: string[] } }).meta
      ?.roles;

    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }

    return requiredRoles.some((role) => userRoles.includes(role));
  });
}

/**
 * 路由性能监控
 */
export class RoutePerformanceMonitor {
  private metrics: Map<string, number[]> = new Map();

  /**
   * 记录路由加载时间
   */
  recordLoadTime(path: string, duration: number): void {
    if (!this.metrics.has(path)) {
      this.metrics.set(path, []);
    }

    const times = this.metrics.get(path)!;
    times.push(duration);

    // 限制记录数量
    if (times.length > 50) {
      times.shift();
    }
  }

  /**
   * 获取平均加载时间
   */
  getAverageLoadTime(path: string): number | undefined {
    const times = this.metrics.get(path);
    if (!times || times.length === 0) {
      return undefined;
    }

    const sum = times.reduce((acc, time) => acc + time, 0);
    return sum / times.length;
  }

  /**
   * 获取所有路由的性能报告
   */
  getPerformanceReport(): Record<
    string,
    {
      count: number;
      average: number;
      min: number;
      max: number;
    }
  > {
    const report: Record<string, any> = {};

    this.metrics.forEach((times, path) => {
      if (times.length > 0) {
        const sum = times.reduce((acc, time) => acc + time, 0);
        report[path] = {
          count: times.length,
          average: sum / times.length,
          min: Math.min(...times),
          max: Math.max(...times),
        };
      }
    });

    return report;
  }

  /**
   * 清空统计数据
   */
  clear(): void {
    this.metrics.clear();
  }
}

export const routePerformanceMonitor = new RoutePerformanceMonitor();
